import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

// Layout Components
import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';
import PublicLayout from '../layouts/PublicLayout';

// Loading Component
import LoadingSpinner from '../Components/common/LoadingSpinner';

// Error Fallback Component
import ErrorFallback from '../Components/common/ErrorFallback';

// Lazy loaded pages for better performance
const HomePage = lazy(() => import('../pages/home/NewHomeScreen'));
const HistoryPage = lazy(() => import('../pages/history/History'));
const SupportPage = lazy(() => import('../pages/support/Support'));
const CreateSupportRequestPage = lazy(() => import('../pages/support/CreateSupportRequest'));
const ProfilePage = lazy(() => import('../pages/profile/CommonProfile'));

// Service Pages
const SendParcelPage = lazy(() => import('../pages/home/SendParcel'));
const DirectionPage = lazy(() => import('../pages/home/DirectionScreen'));
const DirectionTaxiPage = lazy(() => import('../pages/home/DirectionScreenForTaxi'));
const SelectLocationPage = lazy(() => import('../pages/home/SelectLocation'));
const ComfyShuttlePage = lazy(() => import('../pages/home/ComfyShuttle'));
const TouristVehiclesPage = lazy(() => import('../pages/home/TouristVehiclesForm'));
const CustomerOnCallPage = lazy(() => import('../pages/on-call/CustomerOnCallScreen'));
const OnCallVehicleSelectPage = lazy(() => import('../pages/on-call/OnCallVehicleSelectScreen'));

// Booking Pages
const AutoTaxiBookingPage = lazy(() => import('../pages/booking/AutoTaxiBooking'));
const LoadingBookingPage = lazy(() => import('../pages/booking/LoadingBooking'));

// Booking Management
const BiderListPage = lazy(() => import('../pages/home/Bider/BiderListScreen'));
const DriverDetailPage = lazy(() => import('../pages/home/Bider/DriverDetailScreen'));
const ParcelPickupPage = lazy(() => import('../pages/home/Bider/ParcelPickup'));
const PassengerPickupPage = lazy(() => import('../pages/home/Bider/PassengerPickupScreen'));
const MyComfyBookingPage = lazy(() => import('../pages/bookings/MyComfyBooking'));
const UpcomingTripsPage = lazy(() => import('../pages/upcoming-trips/UpcomingTrips'));
const OrderDetailsPage = lazy(() => import('../pages/history/OrderDetails'));

// Comfy Shuttle
const ComfyShuttleScreenPage = lazy(() => import('../pages/comfy-shuttle/ComfyShuttleScreen'));
const SelectSeatPage = lazy(() => import('../pages/comfy-shuttle/SelectSeat'));
const SelectPickupPointPage = lazy(() => import('../pages/comfy-shuttle/SelectPickupPoint'));
const PassengersDetailsPage = lazy(() => import('../pages/comfy-shuttle/PassengersDetails'));

// Payment & Wallet
const PaymentHistoryPage = lazy(() => import('../pages/profile/PaymentHistory'));
const PaymentOptionsPage = lazy(() => import('../pages/dashboard/PaymentOptionsScreen'));
const AddWalletPaymentPage = lazy(() => import('../pages/profile/Addwalletpayment'));
const ComfyPaymentPage = lazy(() => import('../pages/home/Bider/ComfyPayment'));

// Profile Management
const ProfileDetailPage = lazy(() => import('../pages/profile/ProfileDetail'));
const EditVehiclePage = lazy(() => import('../pages/profile/EditVehicleScreen'));
const EditVehicleTypePage = lazy(() => import('../pages/profile/EditVehicleTypeScreen'));
const SelectVehiclePage = lazy(() => import('../pages/add-vehicle/SelectVehicle'));
const AddRegistrationDetailsPage = lazy(() => import('../pages/add-vehicle/AddRegistrationDetails'));
const SelectVehicleTypePage = lazy(() => import('../pages/auth/SelectVehicleTypeScreen'));

// Support & Help
const HelpPage = lazy(() => import('../pages/profile/HelpScreen'));
const AccountIssuesPage = lazy(() => import('../pages/profile/AccountAndAppIssues'));
const HelpWithTripPage = lazy(() => import('../pages/profile/HelpWithAtrip'));
const SafetyPage = lazy(() => import('../pages/profile/SafetyScreen'));

// Legal & Policies
const LegalPoliciesPage = lazy(() => import('../pages/profile/LegalAndPolicies'));
const TermsConditionsPage = lazy(() => import('../pages/profile/TermsAndConditions'));
const PrivacyPolicyPage = lazy(() => import('../pages/profile/PrivacyPolicy'));
const CancellationPolicyPage = lazy(() => import('../pages/profile/CancellationPolicy'));
const OpenSourceLicensesPage = lazy(() => import('../pages/profile/OpenSourceLicenses'));
const BusTicketBookingPrivacyPolicyPage = lazy(() => import('../pages/profile/BusTicketBookingPrivacyPolicy'));
const BusTicketBookingTermsConditionsPage = lazy(() => import('../pages/profile/BusTicketBookingTermsConditions'));

// Other Features
const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsScreen'));
const OffersPage = lazy(() => import('../pages/profile/OffersScreen'));
const ReferEarnPage = lazy(() => import('../pages/profile/ReferAndEarn'));
const RateAppPage = lazy(() => import('../pages/profile/RateOurAppScreen'));
const DocumentsPage = lazy(() => import('../pages/documents/Documents'));
const VersionUpdatePage = lazy(() => import('../pages/home/VersionUpdate'));

// Dashboard & Status
const SelectedPage = lazy(() => import('../pages/dashboard/SelectedScreen'));
const ParcelStatusPage = lazy(() => import('../pages/dashboard/ParcelStatus'));
const DailyEarningsPage = lazy(() => import('../pages/home/DailyEarningsReportScreen'));

// Auth Pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const LoginMethodPage = lazy(() => import('../pages/auth/LoginMethodScreen'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPassword'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));

// Public Pages
const SplashPage = lazy(() => import('../pages/splash/SplashScreen'));
const IntroPage = lazy(() => import('../pages/intro/IntroScreen'));

// Company Pages
const AboutPage = lazy(() => import('../pages/about/About'));
const CareersPage = lazy(() => import('../pages/careers/Careers'));
const BlogPage = lazy(() => import('../pages/blog/Blog'));
const BlogPostPage = lazy(() => import('../pages/blog/BlogPost'));
const PressPage = lazy(() => import('../pages/press/Press'));
const ContactPage = lazy(() => import('../pages/contact/Contact'));
const DriverPartnersPage = lazy(() => import('../pages/driver-partners/DriverPartners'));
const InsurancePage = lazy(() => import('../pages/insurance/Insurance'));

// City Pages
const MumbaiPage = lazy(() => import('../pages/cities/Mumbai'));
const DelhiPage = lazy(() => import('../pages/cities/Delhi'));
const BangalorePage = lazy(() => import('../pages/cities/Bangalore'));

// Route Configuration
const AppRoutes: React.FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="splash" element={<SplashPage />} />
            <Route path="intro" element={<IntroPage />} />
          </Route>

          {/* Auth Routes */}
          <Route path="/auth" element={<AuthLayout />}>
            <Route index element={<Navigate to="/auth/login" replace />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="login-method" element={<LoginMethodPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="select-vehicle-type" element={<SelectVehicleTypePage />} />
          </Route>

                {/* Direct Auth Routes - Redirect to Home with Modal */}
                <Route path="/login" element={<Navigate to="/home?action=login" replace />} />
                <Route path="/register" element={<Navigate to="/home?action=register" replace />} />

          {/* Main Application Routes */}
          <Route path="/" element={<MainLayout />}>
            {/* Core Navigation */}
            <Route path="home" element={<HomePage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="support/create" element={<CreateSupportRequestPage />} />
            <Route path="profile" element={<ProfilePage />} />

            {/* Company Pages */}
            <Route path="about" element={<AboutPage />} />
            <Route path="careers" element={<CareersPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:id" element={<BlogPostPage />} />
            <Route path="press" element={<PressPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="driver-partners" element={<DriverPartnersPage />} />
            <Route path="insurance" element={<InsurancePage />} />

            {/* City Pages */}
            <Route path="city">
              <Route path="mumbai" element={<MumbaiPage />} />
              <Route path="delhi" element={<DelhiPage />} />
              <Route path="bangalore" element={<BangalorePage />} />
            </Route>

            {/* Service Routes */}
            <Route path="services">
              <Route path="send-parcel" element={<SendParcelPage />} />
              <Route path="direction" element={<DirectionPage />} />
              <Route path="direction-taxi" element={<DirectionTaxiPage />} />
              <Route path="select-location" element={<SelectLocationPage />} />
              <Route path="comfy-shuttle" element={<ComfyShuttlePage />} />
              <Route path="tourist-vehicles" element={<TouristVehiclesPage />} />
              <Route path="on-call" element={<CustomerOnCallPage />} />
              <Route path="on-call-vehicle-select" element={<OnCallVehicleSelectPage />} />
            </Route>

            {/* Booking Routes */}
            <Route path="booking">
              <Route path="auto-taxi" element={<AutoTaxiBookingPage />} />
              <Route path="loading" element={<LoadingBookingPage />} />
            </Route>

            {/* Booking Management */}
            <Route path="bookings">
              <Route path="bider-list" element={<BiderListPage />} />
              <Route path="driver-detail" element={<DriverDetailPage />} />
              <Route path="parcel-pickup" element={<ParcelPickupPage />} />
              <Route path="passenger-pickup" element={<PassengerPickupPage />} />
              <Route path="my-comfy-booking" element={<MyComfyBookingPage />} />
              <Route path="upcoming-trips" element={<UpcomingTripsPage />} />
              <Route path="order-details" element={<OrderDetailsPage />} />
            </Route>

            {/* Order Details with parameter */}
            <Route path="order-details/:orderId" element={<OrderDetailsPage />} />

            {/* Comfy Shuttle */}
            <Route path="comfy-shuttle">
              <Route path="screen" element={<ComfyShuttleScreenPage />} />
              <Route path="select-seat" element={<SelectSeatPage />} />
              <Route path="select-pickup-point" element={<SelectPickupPointPage />} />
              <Route path="passengers-details" element={<PassengersDetailsPage />} />
            </Route>

            {/* Payment & Wallet */}
            <Route path="payments">
              <Route path="history" element={<PaymentHistoryPage />} />
              <Route path="options" element={<PaymentOptionsPage />} />
              <Route path="add-wallet" element={<AddWalletPaymentPage />} />
              <Route path="comfy-payment" element={<ComfyPaymentPage />} />
            </Route>

            {/* Profile Management */}
            <Route path="profile">
              <Route path="detail" element={<ProfileDetailPage />} />
              <Route path="edit-vehicle" element={<EditVehiclePage />} />
              <Route path="edit-vehicle-type" element={<EditVehicleTypePage />} />
              <Route path="select-vehicle" element={<SelectVehiclePage />} />
              <Route path="add-registration" element={<AddRegistrationDetailsPage />} />
            </Route>

            {/* Support & Help */}
            <Route path="help">
              <Route index element={<HelpPage />} />
              <Route path="account-issues" element={<AccountIssuesPage />} />
              <Route path="trip-help" element={<HelpWithTripPage />} />
              <Route path="safety" element={<SafetyPage />} />
            </Route>

            {/* Legal & Policies */}
            <Route path="legal">
              <Route path="policies" element={<LegalPoliciesPage />} />
              <Route path="terms" element={<TermsConditionsPage />} />
              <Route path="privacy" element={<PrivacyPolicyPage />} />
              <Route path="cancellation" element={<CancellationPolicyPage />} />
              <Route path="open-source" element={<OpenSourceLicensesPage />} />
              <Route path="bus-ticket-booking-privacy" element={<BusTicketBookingPrivacyPolicyPage />} />
              <Route path="bus-ticket-booking-terms" element={<BusTicketBookingTermsConditionsPage />} />
            </Route>

            {/* Other Features */}
            <Route path="features">
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="offers" element={<OffersPage />} />
              <Route path="refer-earn" element={<ReferEarnPage />} />
              <Route path="rate-app" element={<RateAppPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="version-update" element={<VersionUpdatePage />} />
            </Route>

            {/* Dashboard & Status */}
            <Route path="dashboard">
              <Route path="selected" element={<SelectedPage />} />
              <Route path="parcel-status" element={<ParcelStatusPage />} />
              <Route path="daily-earnings" element={<DailyEarningsPage />} />
            </Route>

            {/* Legacy Routes - for backward compatibility */}
            <Route path="send-parcel" element={<SendParcelPage />} />
            <Route path="direction" element={<DirectionPage />} />
            <Route path="direction-taxi" element={<DirectionTaxiPage />} />
            <Route path="select-location" element={<SelectLocationPage />} />
            <Route path="comfy-shuttle" element={<ComfyShuttlePage />} />
            <Route path="tourist-vehicles-form" element={<TouristVehiclesPage />} />
            <Route path="customer-on-call" element={<CustomerOnCallPage />} />
            <Route path="on-call-vehicle-select" element={<OnCallVehicleSelectPage />} />
            <Route path="bider-list" element={<BiderListPage />} />
            <Route path="driver-detail" element={<DriverDetailPage />} />
            <Route path="parcel-pickup" element={<ParcelPickupPage />} />
            <Route path="passenger-pickup" element={<PassengerPickupPage />} />
            <Route path="my-comfy-booking" element={<MyComfyBookingPage />} />
            <Route path="upcoming-trips" element={<UpcomingTripsPage />} />
            <Route path="order-details" element={<OrderDetailsPage />} />
            <Route path="order-details/:orderId" element={<OrderDetailsPage />} />
            <Route path="comfy-shuttle-screen" element={<ComfyShuttleScreenPage />} />
            <Route path="select-seat" element={<SelectSeatPage />} />
            <Route path="select-pickup-point" element={<SelectPickupPointPage />} />
            <Route path="passengers-details" element={<PassengersDetailsPage />} />
            <Route path="payment-history" element={<PaymentHistoryPage />} />
            <Route path="payment-options" element={<PaymentOptionsPage />} />
            <Route path="add-wallet-payment" element={<AddWalletPaymentPage />} />
            <Route path="comfy-payment" element={<ComfyPaymentPage />} />
            <Route path="profile-detail" element={<ProfileDetailPage />} />
            <Route path="edit-vehicle" element={<EditVehiclePage />} />
            <Route path="edit-vehicle-type" element={<EditVehicleTypePage />} />
            <Route path="select-vehicle" element={<SelectVehiclePage />} />
            <Route path="add-registration-details" element={<AddRegistrationDetailsPage />} />
            <Route path="select-vehicle-screen" element={<SelectVehicleTypePage />} />
            <Route path="select-vehicle-type" element={<SelectVehicleTypePage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="account-and-app-issues" element={<AccountIssuesPage />} />
            <Route path="help-with-trip" element={<HelpWithTripPage />} />
            <Route path="safety" element={<SafetyPage />} />
            <Route path="legal-and-policies" element={<LegalPoliciesPage />} />
            <Route path="terms-and-conditions" element={<TermsConditionsPage />} />
            <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="open-source-licenses" element={<OpenSourceLicensesPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="offers" element={<OffersPage />} />
            <Route path="refer-and-earn" element={<ReferEarnPage />} />
            <Route path="rate-our-app" element={<RateAppPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="version-update" element={<VersionUpdatePage />} />
            <Route path="selected" element={<SelectedPage />} />
            <Route path="parcel-status" element={<ParcelStatusPage />} />
            <Route path="daily-earnings-report" element={<DailyEarningsPage />} />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
